/**
 * Shared hook: fetches active staff from /api/cases/pipeline/team-members
 * (sourced from the staff table) and provides them as pipeline team members.
 *
 * Also exposes a flat name list for simple assignee dropdowns
 * (InvestigationCase step assignment, ComplianceChecklist assignee, etc.)
 *
 * On empty or error there is no hardcoded fallback — add staff in Admin → Employees.
 */
import { useState, useEffect } from 'react'
import { getLocalApiUrl, authFetch } from '../config/api'

export function useStaffMembers({ venueId, includeInactive, includeUsers = false } = {}) {
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [source, setSource] = useState('loading')

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const qs = new URLSearchParams()
        if (venueId) qs.set('venue_id', venueId)
        if (includeInactive) qs.set('include_inactive', 'true')
        if (includeUsers) qs.set('include_users', 'true')
        const res = await authFetch(getLocalApiUrl(`/api/cases/pipeline/team-members?${qs}`))
        const json = await res.json()
        if (cancelled) return
        if (json.success && Array.isArray(json.data)) {
          setMembers(json.data)
          setSource(json.data.length > 0 ? (json.source || 'api') : (json.source || 'none'))
        } else {
          setMembers([])
          setSource('none')
        }
      } catch {
        if (!cancelled) {
          setMembers([])
          setSource('error')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [venueId, includeInactive, includeUsers])

  const assigneeOptions = members.map((m) => ({
    value: m.name,
    label: `${m.name} — ${m.staffRole || m.title}`,
  }))

  const assigneeNames = members.map((m) => m.name)

  return { members, assigneeOptions, assigneeNames, loading, source }
}

export default useStaffMembers
